import React, { useCallback, useMemo, useState } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton, YatPill, YatTile } from '@pkg/components';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { moneyDecimals } from '@pkg/utils';

import { apiFactory } from '@app/client';
import { Cart } from '@app/client/resources';
import { addNotification } from '@app/store/actions/notification';
import { setDeleteCartItemRequestInProgress, setReduxCartItems } from '@app/store/actions/picker';

import './Item.scss';

export interface YatCollectionShelfItemProps {
    yat: string;
    price: number;
    rhythmScore: number;
    taken?: boolean;
    large?: boolean;
    orderItemId: string;
}

const YatCollectionShelfItem = ({
    yat,
    price,
    rhythmScore,
    taken = false,
    large = false,
    orderItemId,
}: YatCollectionShelfItemProps) => {
    const { t } = useTranslation(['yat_picker', 'words']);
    const { secondsRemaining } = useAppSelector((rootState) => rootState.picker);
    const { requestInProgress } = useAppSelector((state) => state.picker);

    const [removed, setRemoved] = useState(false);

    const dispatch = useAppDispatch();

    const handleRemoveFromCart = useCallback(() => {
        dispatch(setDeleteCartItemRequestInProgress(true));
        setRemoved(true);
        apiFactory()
            .call(Cart.deleteOrderItem, { id: orderItemId })
            .then((r) => {
                if (r.ok) {
                    dispatch(setReduxCartItems(r.value));
                }
            })
            .catch((e) => {
                dispatch(
                    addNotification({
                        message: e.message,
                        severity: 'error',
                        title: t('delete_cart_item_failed'),
                    }),
                );
            })
            .finally(() => {
                dispatch(setDeleteCartItemRequestInProgress(false));
            });
    }, [orderItemId, dispatch, t]);

    const showDeleteButton = useMemo(() => {
        return !requestInProgress && secondsRemaining !== null && secondsRemaining !== undefined;
    }, [secondsRemaining, requestInProgress]);

    const containerClasses = cx('yat-collection-shelf-item', {
        'yat-collection-shelf-item--taken': taken,
        'yat-collection-shelf-item--large': large,
        'yat-collection-shelf-item--removed': removed,
    });

    return (
        <section className={containerClasses}>
            <div className="yat-collection-shelf-item__meta">
                {taken ? (
                    <YatPill variant="danger">{t('words:taken')}</YatPill>
                ) : (
                    <YatPill> ${moneyDecimals(price)}</YatPill>
                )}
                {/* { add "Pending" status here as well, just like "taken" } */}
                {showDeleteButton && (
                    <div className="yat-collection-shelf-item__actions">
                        <div>
                            <CircleButton
                                size="small"
                                ariaLabel={t('remove_yat_from_checkout', { yat: yat })}
                                onClick={handleRemoveFromCart}
                            >
                                <CloseIcon />
                            </CircleButton>
                        </div>
                    </div>
                )}
            </div>

            <h2 className="yat-collection-shelf-item__tile-container">
                <YatTile
                    yat={yat}
                    generation={1}
                    shadow
                    rhythmScore={rhythmScore}
                    glow={!taken}
                    showRhythmScore={!taken}
                />
            </h2>
        </section>
    );
};

export default YatCollectionShelfItem;
